import { useEffect, useState, useSyncExternalStore } from "react";
import { AlertTriangle, WifiOff } from "lucide-react";
import { getVerifiedOnline, subscribeConnectivity } from "@/lib/connectivity";
import { FAILED_ROUNDS_BEFORE_ALARM, useHealthQuery } from "@/lib/health-query";
import { cn } from "@/lib/utils";

/**
 * Surfaces connectivity problems to the user.
 *
 * Two distinct states, with user-facing copy (never developer instructions in
 * production):
 *   - Offline (reachability-verified): the device truly has no network. Field
 *     techs are the primary offline users — they get a reassuring "your work is
 *     saved" message, not a scary error. The dedicated OfflineIndicator (global,
 *     fixed, top) and the field SyncBanner are the loud, primary offline signals
 *     and visually sit above this inline banner.
 *   - Online but /health unreachable: the network is up but the API/proxy is
 *     down. Show a neutral "check your connection" message. Only in dev do we
 *     append the operator hint about starting the API on port 3000. This one
 *     is deliberately slow to fire — see `FAILED_ROUNDS_BEFORE_ALARM`. A weak
 *     cellular link is not a down server, and a banner that cries wolf every
 *     45 seconds is one people learn to ignore.
 */
export function ApiStatusBanner({ className }: { className?: string }) {
    // Reachability-verified connectivity, not the raw `navigator.onLine` flag
    // (which false-negatives with full signal). See `connectivity.ts`.
    const online = useSyncExternalStore(subscribeConnectivity, getVerifiedOnline);

    // No point hammering /health while the device knows it is offline.
    const health = useHealthQuery({ enabled: online });

    // One failed round is not evidence. A tech on two bars drops packets all
    // day, and this banner used to shout on the first burst of three attempts
    // crammed into ~2 seconds — so it flapped red and back every 45s while the
    // app underneath worked fine (#1360). Count CONSECUTIVE failed rounds and
    // stay quiet until the link has really given up, the same discipline
    // `connectivity.ts` already applies to the offline signal.
    const [failedRounds, setFailedRounds] = useState(0);
    useEffect(() => {
        if (health.isError) setFailedRounds((n) => n + 1);
    }, [health.isError, health.errorUpdatedAt]);
    useEffect(() => {
        if (health.isSuccess) setFailedRounds(0);
    }, [health.isSuccess, health.dataUpdatedAt]);

    if (!online) {
        return (
            <div
                role="status"
                aria-live="polite"
                className={cn(
                    "flex items-start gap-2 rounded-lg border border-warning/40 bg-card px-4 py-3 text-sm text-warning shadow-sm",
                    className,
                )}
            >
                <WifiOff className="h-4 w-4 shrink-0 mt-0.5" />
                <p>You are offline — your work is saved and will sync when you reconnect.</p>
            </div>
        );
    }

    // Reachable but the server booted against a database that's missing
    // migrations — every feature touching a new column will 500. Louder than
    // any per-page error because the failures look random otherwise.
    const schemaDrift =
        !health.isError &&
        health.data &&
        typeof health.data === "object" &&
        (health.data as { schema_ok?: boolean }).schema_ok === false;
    if (schemaDrift) {
        const pending = (health.data as { pending_migrations?: string[] }).pending_migrations ?? [];
        return (
            <div
                role="alert"
                className={cn(
                    "flex items-start gap-2 rounded-lg border border-destructive/40 bg-card px-4 py-3 text-sm text-destructive shadow-sm",
                    className,
                )}
            >
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                <div>
                    <p className="font-semibold">
                        Server schema is out of date — features may fail until migrations run.
                    </p>
                    <p className="text-destructive/90 mt-0.5">
                        {pending.length} unapplied migration{pending.length === 1 ? "" : "s"}
                        {import.meta.env.DEV ? (
                            <>
                                {" "}
                                — run{" "}
                                <code className="rounded bg-background/80 px-1 py-0.5 text-xs">
                                    npm run db:migrate
                                </code>{" "}
                                and restart the API.
                            </>
                        ) : (
                            " — contact your administrator."
                        )}
                    </p>
                </div>
            </div>
        );
    }

    if (!health.isError || failedRounds < FAILED_ROUNDS_BEFORE_ALARM) return null;

    return (
        <div
            role="alert"
            className={cn(
                "flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive",
                className,
            )}
        >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
                <p className="font-semibold">Cannot reach the server. Please check your connection.</p>
                {import.meta.env.DEV && (
                    <p className="text-destructive/90 mt-0.5">
                        Dev: start the API on port 3000 (and Postgres if needed). Vite proxies{" "}
                        <code className="rounded bg-background/80 px-1 py-0.5 text-xs">/api</code> and{" "}
                        <code className="rounded bg-background/80 px-1 py-0.5 text-xs">/health</code> from the repo
                        root.
                    </p>
                )}
            </div>
        </div>
    );
}
